/**
 * Navigation Store — tracks current route for screen-aware notifications.
 * Updated from _layout.tsx via useSegments().
 */

import { create } from 'zustand';

export interface CalendarFocusRequest {
  id: number;
  dateKey: string;
  viewMode?: 'month' | 'timeline' | 'week' | 'agenda';
  openDayPanel?: boolean;
  animateMonthChange?: boolean;
}

export type CalendarNavigatorViewMode = 'month' | 'timeline' | 'week' | 'agenda';

interface NavigationState {
  currentRoute: string;
  pendingDeepLink: string | null;
  pendingDeepLinkDate: string | null;
  pendingCalendarFocus: CalendarFocusRequest | null;
  nextCalendarFocusId: number;
  tabBarExpanded: boolean;
  tabBarBottomSpace: number;
  tabBarKeyboardVisible: boolean;
  globalDayNavigatorOpen: boolean;
  globalDayNavigatorViewMode: CalendarNavigatorViewMode;
  setCurrentRoute: (route: string) => void;
  setPendingDeepLink: (link: string | null, date?: string | null) => void;
  setTabBarChrome: (chrome: { expanded: boolean; bottomSpace: number }) => void;
  setTabBarKeyboardVisible: (visible: boolean) => void;
  setGlobalDayNavigatorOpen: (open: boolean) => void;
  setGlobalDayNavigatorViewMode: (viewMode: CalendarNavigatorViewMode) => void;
  queueCalendarFocus: (request: Omit<CalendarFocusRequest, 'id'>) => void;
  consumePendingDeepLink: () => string | null;
  consumePendingDeepLinkDate: () => string | null;
  consumePendingCalendarFocus: () => CalendarFocusRequest | null;
}

export const useNavigationStore = create<NavigationState>()((set, get) => ({
  currentRoute: '',
  pendingDeepLink: null,
  pendingDeepLinkDate: null,
  pendingCalendarFocus: null,
  nextCalendarFocusId: 1,
  tabBarExpanded: true,
  tabBarBottomSpace: 0,
  tabBarKeyboardVisible: false,
  globalDayNavigatorOpen: false,
  globalDayNavigatorViewMode: 'month',
  setCurrentRoute: (route: string) => set({ currentRoute: route }),
  setPendingDeepLink: (link: string | null, date?: string | null) => set({ pendingDeepLink: link, pendingDeepLinkDate: date ?? null }),
  setTabBarChrome: ({ expanded, bottomSpace }) => set({ tabBarExpanded: expanded, tabBarBottomSpace: bottomSpace }),
  setTabBarKeyboardVisible: (visible) => set({ tabBarKeyboardVisible: visible }),
  setGlobalDayNavigatorOpen: (open) => set({ globalDayNavigatorOpen: open }),
  setGlobalDayNavigatorViewMode: (viewMode) => set({ globalDayNavigatorViewMode: viewMode }),
  queueCalendarFocus: (request) => set((state) => ({
    nextCalendarFocusId: state.nextCalendarFocusId + 1,
    pendingCalendarFocus: {
      id: state.nextCalendarFocusId,
      ...request,
    },
  })),
  consumePendingDeepLink: () => {
    const link = get().pendingDeepLink;
    if (link) set({ pendingDeepLink: null });
    return link;
  },
  consumePendingDeepLinkDate: () => {
    const date = get().pendingDeepLinkDate;
    if (date) set({ pendingDeepLinkDate: null });
    return date;
  },
  consumePendingCalendarFocus: () => {
    const request = get().pendingCalendarFocus;
    if (request) set({ pendingCalendarFocus: null });
    return request;
  },
}));

/** Check if the user is currently viewing the chat screen */
export const isOnChatScreen = () =>
  useNavigationStore.getState().currentRoute.includes('chat');
